import { ApiError, buildApiUrl, extractApiError, request, requestBlob } from './api';
import {
  isDicomAnalyzeResponse,
  isDicomSeriesRenderResponse,
  type DicomAnalyzeResponse,
  type DicomSeriesRenderResponse,
} from '../types/dicom';

type UploadProgressCallback = (percentage: number) => void;
export type DicomTransferPhase = 'uploading' | 'analyzing' | 'downloading';
type TransferPhaseCallback = (phase: DicomTransferPhase) => void;

function getXhrResponse(xhr: XMLHttpRequest): unknown {
  if (xhr.response !== null && xhr.response !== undefined) {
    return xhr.response;
  }

  if (!xhr.responseText) {
    return null;
  }

  try {
    return JSON.parse(xhr.responseText);
  } catch {
    return xhr.responseText;
  }
}

export async function analyzeDicomFiles(
  files: File[],
  token: string,
  signal?: AbortSignal,
  onProgress?: UploadProgressCallback,
  onPhaseChange?: TransferPhaseCallback
): Promise<DicomAnalyzeResponse> {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file, file.name);
  });

  const response = await new Promise<unknown>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const abortRequest = () => xhr.abort();
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener('abort', abortRequest);
      callback();
    };

    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }

    xhr.open('POST', buildApiUrl('/dicom/analyze'));
    xhr.responseType = 'json';
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = event => {
      if (!event.lengthComputable || event.total <= 0) {
        return;
      }

      onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };
    xhr.upload.onload = () => {
      onProgress?.(100);
      onPhaseChange?.('analyzing');
    };
    xhr.onload = () => {
      const responseBody = getXhrResponse(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        finish(() => resolve(responseBody));
        return;
      }

      finish(() => reject(extractApiError(responseBody, xhr.status)));
    };
    xhr.onerror = () => finish(() => reject(new TypeError('Backend unavailable.')));
    xhr.onabort = () =>
      finish(() => reject(new DOMException('The operation was aborted.', 'AbortError')));

    signal?.addEventListener('abort', abortRequest, { once: true });
    onPhaseChange?.('uploading');
    onProgress?.(0);
    xhr.send(formData);
  });

  if (!isDicomAnalyzeResponse(response)) {
    throw new ApiError('Unknown analysis error.', 500);
  }

  // The POST completes only after the server has written metadata.txt and all
  // convertible NIfTI assets. Read the canonical selection data back from that
  // server-side file instead of continuing with the upload response in memory.
  onPhaseChange?.('downloading');
  const serverAnalysis = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(response.upload_id)}/analysis`,
    { token, signal }
  );
  if (!isDicomAnalyzeResponse(serverAnalysis)) {
    throw new ApiError('Server DICOM metadata could not be verified.', 500);
  }
  return serverAnalysis;
}

export async function analyzeNiftiFile(
  file: File,
  token: string,
  signal?: AbortSignal,
  onProgress?: UploadProgressCallback,
  onPhaseChange?: TransferPhaseCallback
): Promise<DicomAnalyzeResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await new Promise<unknown>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const abortRequest = () => xhr.abort();
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', abortRequest);
      callback();
    };

    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }

    xhr.open('POST', buildApiUrl('/dicom/nifti/analyze'));
    xhr.responseType = 'json';
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    };
    xhr.upload.onload = () => {
      onProgress?.(100);
      onPhaseChange?.('analyzing');
    };
    xhr.onload = () => {
      const responseBody = getXhrResponse(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        finish(() => resolve(responseBody));
      } else {
        finish(() => reject(extractApiError(responseBody, xhr.status)));
      }
    };
    xhr.onerror = () => finish(() => reject(new TypeError('Backend unavailable.')));
    xhr.onabort = () =>
      finish(() => reject(new DOMException('The operation was aborted.', 'AbortError')));
    signal?.addEventListener('abort', abortRequest, { once: true });
    onPhaseChange?.('uploading');
    onProgress?.(0);
    xhr.send(formData);
  });

  if (!isDicomAnalyzeResponse(response) || response.source_type !== 'nifti') {
    throw new ApiError('Server NIfTI metadata could not be verified.', 500);
  }

  onPhaseChange?.('downloading');
  return getDicomUploadAnalysis(response.upload_id, token, signal);
}

export async function getDicomUploadAnalysis(
  uploadId: string,
  token: string,
  signal?: AbortSignal
): Promise<DicomAnalyzeResponse> {
  const response = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(uploadId)}/analysis`,
    { token, signal }
  );
  if (!isDicomAnalyzeResponse(response)) {
    throw new ApiError('Server DICOM metadata could not be verified.', 500);
  }
  return response;
}

export async function getDicomSeries(
  uploadId: string,
  studyInstanceUid: string,
  seriesInstanceUid: string,
  token: string,
  signal?: AbortSignal
): Promise<DicomSeriesRenderResponse> {
  const response = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(uploadId)}/studies/${encodeURIComponent(
      studyInstanceUid
    )}/series/${encodeURIComponent(seriesInstanceUid)}`,
    { token, signal }
  );

  if (!isDicomSeriesRenderResponse(response)) {
    throw new ApiError('Series image data could not be loaded.', 500);
  }

  return response;
}

function normalizeApiPath(path: string) {
  const apiPrefix = '/api/v1';
  return path.startsWith(apiPrefix) ? path.slice(apiPrefix.length) : path;
}

export function getDicomInstanceBlob(
  instanceUrl: string,
  token: string,
  signal?: AbortSignal
): Promise<Blob> {
  return requestBlob(normalizeApiPath(instanceUrl), {
    token,
    signal,
  });
}

export async function getDicomSeriesThumbnailBlob(
  uploadId: string,
  studyInstanceUid: string,
  seriesInstanceUid: string,
  token: string,
  signal?: AbortSignal
): Promise<Blob> {
  const series = await getDicomSeries(uploadId, studyInstanceUid, seriesInstanceUid, token, signal);
  const thumbnailInstance = series.instances[Math.floor(series.instances.length / 2)];

  if (!thumbnailInstance) {
    throw new ApiError('The series does not contain a thumbnail image.', 422);
  }

  return getDicomInstanceBlob(thumbnailInstance.instance_url, token, signal);
}

export function deleteDicomUpload(uploadId: string, token: string) {
  return request<void>(`/dicom/uploads/${uploadId}`, {
    method: 'DELETE',
    token,
  });
}
